'use client';

import { Box } from '@terra/ui-styled-system';
import { useEffect, useRef, type ReactNode } from 'react';
import { Menu } from '../../../components/menu';
import {
  getMentionsMenuFloatingCoords,
  type MentionsMenuAnchorPosition,
} from './mentions-menu-positioning';
import { MentionsMenuPortal } from './MentionsMenuPortal';

type MentionsMenuRootProps = {
  open: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  position?: MentionsMenuAnchorPosition;
  trigger?: ReactNode;
  children: ReactNode;
};

function MentionsMenuFloatingShell({
  open,
  position,
  onOpenChange,
  children,
}: {
  open: boolean;
  position: MentionsMenuAnchorPosition;
  onOpenChange: (details: { open: boolean }) => void;
  children: ReactNode;
}) {
  const shellRef = useRef<HTMLDivElement>(null);
  const { left, top, maxHeight } = getMentionsMenuFloatingCoords(position);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (shellRef.current?.contains(target)) {
        return;
      }
      onOpenChange({ open: false });
    };

    document.addEventListener('pointerdown', handlePointerDown, true);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [open, onOpenChange]);

  if (!open) {
    return null;
  }

  return (
    <MentionsMenuPortal>
      <Box
        ref={shellRef}
        position="fixed"
        zIndex="popover"
        display="flex"
        flexDirection="column"
        style={{
          left,
          top,
          maxHeight,
          height: maxHeight,
        }}
        minW="240px"
        maxW="320px"
        overflow="hidden"
        bg="bg.default"
        borderWidth="1px"
        borderColor="border.subtle"
        borderRadius="md"
        boxShadow="md"
      >
        {children}
      </Box>
    </MentionsMenuPortal>
  );
}

export function MentionsMenuRoot({
  open,
  onOpenChange,
  position,
  trigger,
  children,
}: MentionsMenuRootProps) {
  if (!open) {
    return null;
  }

  if (trigger) {
    return (
      <Menu.Root
        open={open}
        onOpenChange={onOpenChange}
        positioning={{
          placement: 'bottom-start',
          strategy: 'fixed',
          flip: true,
          slide: true,
          offset: { mainAxis: 6, crossAxis: 0 },
        }}
        lazyMount
        unmountOnExit
      >
        <Menu.Trigger asChild>{trigger}</Menu.Trigger>
        <MentionsMenuPortal>{children}</MentionsMenuPortal>
      </Menu.Root>
    );
  }

  if (!position) {
    return null;
  }

  return (
    <MentionsMenuFloatingShell
      open={open}
      position={position}
      onOpenChange={onOpenChange}
    >
      {children}
    </MentionsMenuFloatingShell>
  );
}
